import { useEffect, useState, useRef } from "react";
import api, { apiError, imageById } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea, Toggle, Modal, Confirm, Empty } from "@/pages/admin/managers/ui";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";

export default function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const dragId = useRef(null);

  const load = async () => {
    const { data } = await api.get("/categories?all=true");
    setCats(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.name?.trim()) return toast.error("El nombre es obligatorio.");
    try {
      const body = { name: modal.name, description: modal.description || "", is_visible: modal.is_visible !== false };
      if (modal.id) await api.put(`/admin/categories/${modal.id}`, body);
      else await api.post("/admin/categories", body);
      toast.success("Categoría guardada.");
      setModal(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/categories/${confirmId}`);
      toast.success("Categoría eliminada.");
      setConfirmId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = async (targetId) => {
    if (!dragId.current || dragId.current === targetId) return;
    const from = cats.findIndex((c) => c.id === dragId.current);
    const to = cats.findIndex((c) => c.id === targetId);
    const next = [...cats];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    setCats(next);
    dragId.current = null;
    await api.put("/admin/categories/reorder", { ids: next.map((c) => c.id) });
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Btn data-testid="add-category-btn" onClick={() => setModal({ is_visible: true })}><Plus size={16} className="inline mr-1" /> Nueva categoría</Btn>
      </div>
      {cats.length === 0 ? <Empty>No hay categorías.</Empty> : (
        <div className="space-y-2">
          {cats.map((c) => (
            <div
              key={c.id}
              data-testid={`category-row-${c.id}`}
              draggable
              onDragStart={() => (dragId.current = c.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(c.id)}
              className="flex items-center gap-4 bg-[#18181B] border border-[#27272A] rounded-lg p-4"
            >
              <GripVertical size={18} className="text-[#52525B] cursor-grab shrink-0" />
              {(c.cover_url || c.cover_photo_id) ? <img src={c.cover_url || imageById(c.cover_photo_id, true)} alt="" className="w-12 h-12 rounded object-cover shrink-0" /> : <div className="w-12 h-12 rounded bg-[#27272A] shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium">{c.name} {!c.is_visible && <span className="text-xs text-[#8C8375]">(oculta)</span>}</p>
                <p className="text-xs text-[#8C8375] truncate">{c.description} · {c.photo_count} fotos</p>
              </div>
              <button data-testid={`category-edit-${c.id}`} onClick={() => setModal(c)} className="text-[#A1A1AA] hover:text-[#C5A059]"><Pencil size={16} /></button>
              <button data-testid={`category-delete-${c.id}`} onClick={() => setConfirmId(c.id)} className="text-[#A1A1AA] hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.id ? "Editar categoría" : "Nueva categoría"} testid="category-modal">
        {modal && (
          <>
            <Field label="Nombre"><Input data-testid="category-name-input" value={modal.name || ""} onChange={(e) => setModal({ ...modal, name: e.target.value })} /></Field>
            <Field label="Descripción"><Textarea rows={2} value={modal.description || ""} onChange={(e) => setModal({ ...modal, description: e.target.value })} /></Field>
            <Toggle testid="category-visible-toggle" checked={modal.is_visible !== false} onChange={(v) => setModal({ ...modal, is_visible: v })} label="Visible en el sitio" />
            <div className="flex justify-end gap-3 pt-2">
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
              <Btn data-testid="category-save-btn" onClick={save}>Guardar</Btn>
            </div>
          </>
        )}
      </Modal>
      <Confirm open={!!confirmId} onClose={() => setConfirmId(null)} onConfirm={del} message="¿Eliminar esta categoría? Debe estar vacía (sin fotografías)." />
    </div>
  );
}
